/**
 * D244: the passing effects, a number that pops and coins that fly to the counter. Each is a
 * component in its own right, and each can be fired from an event handler with `popNumber`
 * and `flyCoins`, which draw into the one `JuiceLayer` the app mounts at its root.
 */
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  coinTarget,
  centreOf,
  prefersReducedMotion,
  spread,
  useReducedMotion,
  type Point,
} from './motion.ts';
import { playSound, type SoundName } from './sound.ts';

/** How long each effect lasts, in milliseconds. A still effect stays this long too. */
export const POP_MS = 800;
/** D431: a gain floats up 44px over 1.1s, in the good-news green. */
export const GAIN_MS = 1100;
/** D431: a coin flies for 1.1s; twelve leave 50ms apart, and the counter bumps at 900ms. */
export const COIN_MS = 1100;
/** D457: a tap's pop rises from the circle's top right for 600ms. */
export const CORNER_MS = 600;
export const COUNTER_BUMP_MS = 900;
/** The gap between one coin and the next leaving. */
export const COIN_STAGGER_MS = 50;

/** Where an effect starts: a point, an element, or the tap itself. */
export type Origin = Point | Element | { clientX: number; clientY: number };

export function toPoint(o: Origin): Point {
  if ('clientX' in o) return { x: o.clientX, y: o.clientY };
  if ('x' in o && 'y' in o && typeof o.x === 'number') return { x: o.x, y: o.y };
  return centreOf(o as Element);
}

// ---------------------------------------------------------------------------------------

export interface NumberPopProps {
  x: number;
  y: number;
  /** What pops, already formatted: `+5c`, `+12.3Kc`. */
  text: string;
  onDone?: () => void;
  /** D457: rise from the point's top right instead of its centre. */
  corner?: boolean;
}

/** A figure, `+5c`, `x2`, `+1`, `-12.3Kc/s`: drawn in the number font. Anything else is words. */
export const isFigure = (text: string): boolean =>
  /^[+\-x]?[\d.,]+(e\d+)?[KMBT]?c?(\/s)?$/.test(text);

/** A number that floats up and fades from a point. Still, it shows and goes. */
/** A gain, `+12.1Bt`: it floats up in green (D431). */
export const isGain = (text: string): boolean => text.startsWith('+') && isFigure(text);

export function NumberPop({ x, y, text, onDone, corner }: NumberPopProps) {
  const gain = isGain(text);
  useEffect(() => {
    if (!onDone) return;
    const t = setTimeout(onDone, corner ? CORNER_MS : gain ? GAIN_MS : POP_MS);
    return () => clearTimeout(t);
  }, []);
  return (
    <span
      class={
        corner
          ? 'juice-pop juice-pop-corner'
          : gain
            ? 'juice-pop juice-pop-gain'
            : isFigure(text)
              ? 'juice-pop'
              : 'juice-pop juice-pop-words'
      }
      style={{ left: `${x}px`, top: `${y}px` }}
      aria-hidden="true"
    >
      {text}
    </span>
  );
}

/** The flight of each coin, for `CoinFly`. Exported for the tests. */
export function coinFlights(
  from: Point,
  to: Point,
  count: number,
): { x: number; y: number; dx: number; dy: number; delay: number }[] {
  const n = Math.max(1, Math.min(12, Math.round(count)));
  return Array.from({ length: n }, (_, i) => {
    const jx = (spread(i, 1.7) - 0.5) * 24;
    const jy = (spread(i, 2.3) - 0.5) * 24;
    const x = from.x + jx;
    const y = from.y + jy;
    return { x, y, dx: to.x - x, dy: to.y - y, delay: i * COIN_STAGGER_MS };
  });
}

export interface CoinFlyProps {
  from: Point;
  /** Where the coins land. The element marked `data-coin-target` when left out. */
  to?: Point;
  /** How many coins, 1 to 12. */
  count?: number;
  onDone?: () => void;
}

/** A few coins arcing from a point to the coin counter. Still, nothing is drawn. */
export function CoinFly({ from, to, count = 5, onDone }: CoinFlyProps) {
  const reduced = useReducedMotion();
  const [target] = useState(() => to ?? coinTarget());
  const flights = target && !reduced ? coinFlights(from, target, count) : [];
  const total = flights.length ? COIN_MS + flights[flights.length - 1]!.delay : 0;
  useEffect(() => {
    // D431: the counter bumps to 108% as the first coins land.
    const bump = flights.length
      ? setTimeout(() => {
          document
            .querySelector('[data-coin-target]')
            ?.animate?.(
              [{ transform: 'scale(1)' }, { transform: 'scale(1.08)' }, { transform: 'scale(1)' }],
              { duration: 300, easing: 'ease' },
            );
        }, COUNTER_BUMP_MS)
      : undefined;
    const t = onDone ? setTimeout(onDone, total) : undefined;
    return () => {
      clearTimeout(bump);
      clearTimeout(t);
    };
  }, []);
  return (
    <>
      {flights.map((f, i) => (
        <span
          key={i}
          class="juice-coin"
          aria-hidden="true"
          style={
            {
              left: `${f.x}px`,
              top: `${f.y}px`,
              '--dx': `${f.dx}px`,
              '--dy': `${f.dy}px`,
              '--juice-coin-ms': `${COIN_MS}ms`,
              animationDelay: `${f.delay}ms`,
            } as JSX.CSSProperties
          }
        >
          <span style={{ animationDelay: `${f.delay}ms` }} />
        </span>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------------------
// Fired from a handler.

type Effect =
  | { id: number; kind: 'pop'; at: Point; text: string; corner?: boolean }
  | { id: number; kind: 'coins'; from: Point; to?: Point; count: number }
  | {
      id: number;
      kind: 'milestone';
      at: Point;
      bar: DOMRect | null;
      sign: Point | null;
      text: string;
      burst: boolean;
    };

let effects: Effect[] = [];
let nextId = 1;
const subscribers = new Set<(e: Effect[]) => void>();

function emit(): void {
  for (const fn of subscribers) fn(effects);
}

function add(e: Effect): void {
  // A cap, so a stuck finger cannot pile up hundreds of nodes.
  effects = [...effects.slice(-40), e];
  emit();
}

function remove(id: number): void {
  effects = effects.filter((e) => e.id !== id);
  emit();
}

/**
 * Pop `text` from a point, an element or a tap. Needs a `JuiceLayer` on the page. A float that
 * says what a notice on show or waiting already says is dropped (D260).
 */
export function popNumber(at: Origin, text: string): void {
  if (announced(text)) return;
  add({ id: nextId++, kind: 'pop', at: toPoint(at), text });
}

/**
 * D457: a tap's pop, out of the element's top right (never over its icon, name or Buy). Quick taps
 * stack 10px right and 12px up from the one before, at most three at once; older ones go.
 */
export function popFromCorner(el: Element, text: string): void {
  if (announced(text)) return;
  const r = el.getBoundingClientRect();
  const live = effects.filter((e) => e.kind === 'pop' && e.corner);
  const n = Math.min(live.length, 2);
  if (live.length >= 3) remove(live[0]!.id);
  add({
    id: nextId++,
    kind: 'pop',
    at: { x: r.right + 10 * n, y: r.top - 12 * n },
    text,
    corner: true,
  });
}

/** D461: a sheet or a dialog is open (both are `role="dialog"`). */
function dialogOpen(): boolean {
  return typeof document !== 'undefined' && document.querySelector('[role="dialog"]') !== null;
}

/**
 * Fly coins from a point to the counter. Needs a `JuiceLayer` on the page. D461: none start
 * while a sheet or a dialog (Welcome back) is open; the coins are dropped, not saved up.
 */
export function flyCoins(from: Origin, opts: { count?: number; to?: Origin } = {}): void {
  if (dialogOpen()) return;
  const e: Effect = { id: nextId++, kind: 'coins', from: toPoint(from), count: opts.count ?? 5 };
  if (opts.to) e.to = toPoint(opts.to);
  add(e);
}

/** How long the milestone's ring, bar and sign last, and its row glow. */
export const MILESTONE_MS = 1400;

/**
 * D431: a milestone reached on `row`. A 2px accent ring spreads from the row to 360px over 1.2s,
 * the bar fills to the end (900ms), the sign pops above the tick (500ms, with overshoot) and the
 * row glows once in the coin colour. `signs` is the Settings switch (D305): off, no ring, bar,
 * sign or glow. Still (reduce motion), the sign alone is drawn, at its end state.
 */
export function celebrateMilestone(row: Element, opts: { text: string; signs: boolean }): void {
  if (!opts.signs) return;
  const tick = row.querySelector('[data-milestone-tick]');
  const bar = row.querySelector('[data-milestone-bar]');
  add({
    id: nextId++,
    kind: 'milestone',
    at: centreOf(row),
    bar: bar ? bar.getBoundingClientRect() : null,
    sign: tick ? centreOf(tick) : null,
    text: opts.text,
    burst: !prefersReducedMotion(),
  });
  if (!prefersReducedMotion())
    row.animate?.(
      [
        { boxShadow: '0 0 0 0 transparent' },
        { boxShadow: '0 0 0 6px color-mix(in srgb, var(--coin) 45%, transparent)', offset: 0.3 },
        { boxShadow: '0 0 0 0 transparent' },
      ],
      { duration: MILESTONE_MS, delay: 200 },
    );
}

function MilestoneFx({
  e,
  onDone,
}: {
  e: Extract<Effect, { kind: 'milestone' }>;
  onDone: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDone, MILESTONE_MS + 200);
    return () => clearTimeout(t);
  }, []);
  return (
    <>
      {e.burst && (
        <span
          class="mo-ring"
          aria-hidden="true"
          style={{ left: `${e.at.x}px`, top: `${e.at.y}px` }}
        />
      )}
      {e.burst && e.bar && (
        <span
          class="mo-barfill"
          aria-hidden="true"
          style={{
            left: `${e.bar.left}px`,
            top: `${e.bar.top}px`,
            width: `${e.bar.width}px`,
            height: `${e.bar.height}px`,
          }}
        />
      )}
      {e.sign && (
        <span
          class="mo-sign"
          aria-hidden="true"
          style={{ left: `${e.sign.x}px`, top: `${e.sign.y - 16}px` }}
        >
          {e.text}
        </span>
      )}
    </>
  );
}

/** The layer the fired effects draw into. Mount once, at the app's root. */
export function JuiceLayer() {
  const [list, setList] = useState(effects);
  useEffect(() => {
    subscribers.add(setList);
    return () => void subscribers.delete(setList);
  }, []);
  if (!list.length) return null;
  return (
    <div class="juice-layer">
      {list.map((e) =>
        e.kind === 'pop' ? (
          <NumberPop
            key={e.id}
            x={e.at.x}
            y={e.at.y}
            text={e.text}
            corner={e.corner}
            onDone={() => remove(e.id)}
          />
        ) : e.kind === 'milestone' ? (
          <MilestoneFx key={e.id} e={e} onDone={() => remove(e.id)} />
        ) : (
          <CoinFly
            key={e.id}
            from={e.from}
            {...(e.to ? { to: e.to } : {})}
            count={e.count}
            onDone={() => remove(e.id)}
          />
        ),
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// D260: one celebration at a time. Toasts and banners wait in one queue and show one by one,
// each for `NOTICE_MS`, and only while a `NoticeHost` is on screen to show them.

export const NOTICE_MS = 2500;

export interface Notice {
  id: number;
  /** The headline. A float with the same words is dropped. */
  text: string;
  /** A second, smaller line: what a goal pays. */
  sub?: string;
  /** A toast is one short line; a banner is wider and has the second line. */
  kind: 'toast' | 'banner';
  /** For the e2e: `milestone-toast`, `goal-met`. */
  testId: string;
  /** Played as it shows. */
  sound?: SoundName;
  /** Coins that fly from the notice to the counter as it shows. */
  coins?: number;
  /** D374: higher shows first; the default is 0. */
  priority?: number;
  /** D431: how long it stays, when not `NOTICE_MS`. The milestone toast stays 3.6s. */
  ms?: number;
  /** D374: notices of one group that wait together merge into one, by `join`. */
  group?: string;
  join?: (a: NoticeBody, b: NoticeBody) => NoticeBody;
  /**
   * D374: asked as the notice reaches the screen. It returns the words in the open world's
   * names, or null when the notice is no longer true, and then it is dropped.
   */
  check?: () => NoticeBody | null;
}

/** What a notice says, worked out when it reaches the screen. */
export interface NoticeBody {
  text: string;
  sub?: string;
}

let notices: Notice[] = [];
/** The notice the host has on screen; a merge never reaches into it. */
let onShow: number | null = null;
const noticeSubs = new Set<(n: Notice[]) => void>();

function emitNotices(): void {
  for (const fn of noticeSubs) fn(notices);
}

/** What a notice says now: its check's words, or its own when it has no check. */
export function noticeBody(n: Pick<Notice, 'text' | 'sub' | 'check'>): NoticeBody | null {
  return n.check ? n.check() : { text: n.text, ...(n.sub ? { sub: n.sub } : {}) };
}

/**
 * Queue a notice. One already waiting with the same words is not queued twice. D374: one of the
 * same group already waiting (never the one on show) takes it in, and the two read as one.
 */
export function notify(n: Omit<Notice, 'id'>): void {
  if (notices.some((q) => q.text === n.text)) return;
  const into =
    n.group && n.join ? notices.find((q) => q.group === n.group && q.id !== onShow) : undefined;
  if (into && n.join) {
    const join = n.join;
    const first = into;
    const merged: Notice = {
      ...first,
      ...join(
        { text: first.text, ...(first.sub ? { sub: first.sub } : {}) },
        { text: n.text, ...(n.sub ? { sub: n.sub } : {}) },
      ),
      priority: Math.max(first.priority ?? 0, n.priority ?? 0),
      check: () => {
        const a = noticeBody(first);
        const b = noticeBody(n);
        return a && b ? join(a, b) : (a ?? b);
      },
    };
    notices = notices.map((q) => (q === first ? merged : q));
    emitNotices();
    return;
  }
  notices = [...notices, { ...n, id: nextId++ }];
  emitNotices();
}

/** The notice to show next: the most important, the earliest among equals. */
export function nextNotice(list: readonly Notice[] = notices): Notice | undefined {
  let best: Notice | undefined;
  for (const q of list) if (!best || (q.priority ?? 0) > (best.priority ?? 0)) best = q;
  return best;
}

/** The host has this notice on screen. */
export function markShown(id: number | null): void {
  onShow = id;
}

/** The notices on show and waiting, the one on show first. */
export function noticeQueue(): readonly Notice[] {
  return notices;
}

/** A notice on show or waiting already says `text`. */
export function announced(text: string): boolean {
  return notices.some((q) => q.text === text);
}

/** The notice on show is done: the next one shows. */
export function finishNotice(id: number): void {
  if (onShow === id) onShow = null;
  notices = notices.filter((q) => q.id !== id);
  emitNotices();
}

/** Drop every notice. For tests, and for a world that changes under the screen. */
export function clearNotices(): void {
  notices = [];
  onShow = null;
  emitNotices();
}

/**
 * Where the notices show: the first in the queue, for `NOTICE_MS`, then the next. While it is
 * not mounted, or `paused` (a fanfare has the screen), the queue waits.
 */
export function NoticeHost({ paused = false }: { paused?: boolean }) {
  const [list, setList] = useState(notices);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    noticeSubs.add(setList);
    setList(notices);
    return () => void noticeSubs.delete(setList);
  }, []);
  // D374: the one on show stays; otherwise the most important waiting.
  const head = paused ? undefined : (list.find((q) => q.id === onShow) ?? nextNotice(list));
  const [shown, setShown] = useState<{ id: number; body: NoticeBody } | null>(null);
  useEffect(() => {
    if (!head) return;
    // D374: checked as it reaches the screen; one no longer true is dropped unseen.
    const body = noticeBody(head);
    if (!body) return void finishNotice(head.id);
    markShown(head.id);
    setShown({ id: head.id, body });
    if (head.sound) playSound(head.sound);
    if (head.coins && ref.current) flyCoins(ref.current, { count: head.coins });
    const t = setTimeout(() => finishNotice(head.id), head.ms ?? NOTICE_MS);
    return () => clearTimeout(t);
  }, [head?.id]);
  if (!head || shown?.id !== head.id) return null;
  const body = shown.body;
  const at = head.kind === 'toast' ? body.text.indexOf(': ') : -1;
  const split = at > 0 ? [body.text.slice(0, at), body.text.slice(at + 2)] : null;
  return (
    <div
      key={head.id}
      ref={ref}
      role="status"
      data-notice=""
      data-testid={head.testId}
      class={head.kind === 'banner' ? 'juice-notice juice-notice-banner' : 'juice-notice'}
      style={{ '--notice-ms': `${head.ms ?? NOTICE_MS}ms` } as JSX.CSSProperties}
    >
      {split ? (
        // D431: a toast reads `Wheat at 10` on the left and what it pays on the right.
        <div class="juice-notice-text juice-notice-split">
          <span>{split[0]}:</span> <span class="num">{split[1]}</span>
        </div>
      ) : (
        <div class="juice-notice-text">{body.text}</div>
      )}
      {body.sub && <div class="juice-notice-sub">{body.sub}</div>}
    </div>
  );
}
